import { ActionButton } from './../action-button-toolbar/action-button-toolbar.component';
import { Component, OnInit, Input, ViewChild, ElementRef, AfterViewInit, AfterViewChecked, OnDestroy, EventEmitter, Output } from '@angular/core';
import { Observable, combineLatest, Subject, Subscription } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map, startWith } from 'rxjs/operators';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ExcelService } from 'src/app/shared/excel';
import { ActionButtonEvent } from '../action-button-toolbar/action-button-toolbar.component';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

export interface ColumnSetting {
  columnId: string
  name: string
  titlecase?: boolean
  hideOnMobile?: boolean
  maxLengthOnMobile?: number
  align?: string,
  type?: string,
  class?: string,
  hidden?: boolean,
  isNumber?: boolean,
  listener?: EventEmitter<any>

}


@Component({
  selector: 'app-table-standard-layout',
  templateUrl: './table-standard-layout.component.html',
  styleUrls: ['./table-standard-layout.component.css'],
})
export class TableStandardLayoutComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {


  @Input() dataSource$: Observable<any>;
  dataSourceFiltered$: Observable<MatTableDataSource<any>>;
  @Input() columnSettings: ColumnSetting[];
  @Input() rowActionButtons: ActionButton[];
  @Input() topbarActionButton: ActionButton[];
  @Input() title: string;
  @Input() showExport: boolean = true;
  @Input() forceLoading: boolean = false;
  @Input() showImport: boolean;
  @Output() onImport = new EventEmitter<ActionButtonEvent>();
  @Input() param;
  @Input() sortingArray;
  @Input() sortByColumn: string;
  @Input() scrollable: boolean = false;
  @Input() searchFields?: string[];
  @Input() showBuiltInSearch: boolean = true;

  // @ViewChild('TABLE', { static: false }) table: ElementRef;
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  onExport: EventEmitter<ActionButtonEvent>;
  onExportSub: Subscription;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(['(max-width: 960px)'])
    .pipe(
      map(result => result.matches)
    );
  handsetSub: any;
  isMobile: boolean;
  filter$: Subject<string>;
  value: string = "";
  exportData: any;
  displayedColumnsList: string[] = [];

  constructor(private breakpointObserver: BreakpointObserver, private excelService: ExcelService) {

  }

  ngOnInit() {
    if (this.sortByColumn) {
      this.dataSource$ = this.dataSource$.pipe(map(dataArray => dataArray.sort(
        (a, b) => {
          if (a[this.sortByColumn] < b[this.sortByColumn])
            return -1;
          if (a[this.sortByColumn] > b[this.sortByColumn])
            return 1;
          return 0;
        }
      )))
    }


    if (this.showExport) {

      this.onExport = new EventEmitter<ActionButtonEvent>();

      this.onExportSub = this.onExport.subscribe(() => this.exportToExcel())

      if (!this.topbarActionButton) this.topbarActionButton = [];

      this.topbarActionButton.unshift(
        { icon: 'save_alt', tooltip: 'Export', eventEmitter: this.onExport }
      )
    }
    if (this.showImport) {
      this.topbarActionButton.unshift(
        { icon: 'vertical_align_top', tooltip: 'Import', eventEmitter: this.onImport },
      )
    }

    this.updateDisplayedColumns();
  }
  ngAfterViewInit() {

    this.handsetSub = this.isHandset$.subscribe(isMobile => {
      this.isMobile = isMobile;
      this.updateDisplayedColumns();
    })

    this.filter$ = new Subject();

    this.dataSourceFiltered$ = combineLatest(
      this.dataSource$, this.filter$.pipe(startWith(""))).pipe(map(([data, filter]) => {
        let mtds = new MatTableDataSource(data);
        mtds.paginator = this.paginator;
        mtds.sort = this.sort;
        this.exportData = data;
        if (this.searchFields?.length) {
          // MatTableDataSource doesn't normalise the filter for a custom
          // predicate (only its default one does), so trim/lowercase here.
          mtds.filterPredicate = (row, filterValue) => {
            const term = filterValue.trim().toLowerCase();
            return this.searchFields.some(field => (row[field] ?? '').toString().toLowerCase().includes(term));
          };
        }
        if (filter) mtds.filter = filter;
        // The <table matSort> only exists once this observable has already
        // emitted once (it's behind *ngIf="dataSourceFiltered$ | async"), so
        // on the very first emission this.sort/this.paginator are still
        // undefined - the ViewChild can't resolve against an element that
        // hasn't rendered yet. That leaves this first MatTableDataSource
        // permanently without a sort/paginator, so clicking a column header
        // silently does nothing until some later event rebuilds the data
        // source (by which point the ViewChild has caught up) - "sorting
        // only works sometimes". ngAfterViewChecked below backfills them
        // onto whichever data source is current once they do resolve.
        this.currentDataSource = mtds;
        return mtds;
      }));

  }

  private currentDataSource: MatTableDataSource<any>;

  ngAfterViewChecked() {
    if (this.currentDataSource) {
      if (this.sort && this.currentDataSource.sort !== this.sort) {
        this.currentDataSource.sort = this.sort;
      }
      if (this.paginator && this.currentDataSource.paginator !== this.paginator) {
        this.currentDataSource.paginator = this.paginator;
      }
    }
  }

  applyFilter(filterValue: string) {
    this.filter$.next(filterValue);
  }

  shortenString(text: string, length: number) {
    if (length && this.isMobile)
      if (length > text.length) {
        return text;
      } else {
        return text.substr(0, length) + '...';
      }
    else
      return text;
  }

  toolTipFilter(text: string, length: number) {
    if (length && this.isMobile) {
      if (length > text.length) {
        return "";
      } else {
        return text;
      }
    } else {
      return "";
    }
  }

  displayedColumns() {
    if (!this.isMobile)
      return this.addActions(this.columnSettings.filter(columnSetting => !columnSetting.hidden).map(columnSetting => columnSetting.columnId));
    else
      return this.addActions(this.columnSettings.filter(columnSettings => !columnSettings.hideOnMobile).filter(columnSetting => !columnSetting.hidden).map(columnSetting => columnSetting.columnId));
  }

  private updateDisplayedColumns() {
    this.displayedColumnsList = this.displayedColumns();
  }

  trackByRow = (index: number, item: any) => {
    return item?.id ?? item?.orderId ?? item?.companyId ?? index;
  }

  addActions(columns: string[]) {

    if (this.rowActionButtons) columns.push('actionButtons')
    return columns;
  }

  exportToExcel() {

    // Build every row in columnSettings order. Taking the keys from the data
    // itself made the column order follow whatever field order each document
    // happened to have, so two exports of the same table could differ.
    const fixedData = this.exportData.map(row => {
      const line = {};
      this.columnSettings.forEach(c => {
        let value = row[c.columnId];
        if (c.isNumber && value !== null && value !== undefined && !isNaN(parseFloat(value))) {
          value = parseFloat(value);
        }
        line[c.name] = value ?? '';
      });
      return line;
    });


    this.excelService.exportAsExcelFile(fixedData, this.title + " - " + moment().tz('Europe/Oslo').format('DD-MM-YYYY_HH:mm:ssZZ'), this.param, this.sortingArray);

  }



  updateRow($event: Event,element,emitter) {

    // console.log("click",$event,emitter)

    emitter.emit({element,eventValue : $event})
  }

  click(listener,element){
    listener?.emit(element)
  }

  ngOnDestroy() {
    if (this.onExportSub)
      this.onExportSub.unsubscribe();
    if (this.handsetSub)
      this.handsetSub.unsubscribe();
    if (this.filter$)
      this.filter$.complete();
  }

}
